import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Container, FormControl,
  FormControlLabel, InputLabel, MenuItem, Paper, Radio, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { useSelector } from 'react-redux';
import Navbar from '../setup/Navbar';
import '../../services/GlobalVariables';

export default function UserBranchAssignments() {
  const currentUser = useSelector((state) => state.auth.user);
  const [data, setData] = useState({ users: [], branches: [], counters: [], mappings: [] });
  const [selectedUserID, setSelectedUserID] = useState('');
  const [assignments, setAssignments] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadData = useCallback(async () => {
    if (!currentUser?.cmpID) return;
    setLoading(true);
    try {
      const response = await axios.post(global.SET_API_URL + 'users/getBranchAssignments', { cmpID: currentUser.cmpID });
      setData(response.data);
      setSelectedUserID((value) => value || response.data.users?.[0]?.userID || '');
      setMessage({ type: '', text: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Unable to load assignments' });
    } finally {
      setLoading(false);
    }
  }, [currentUser?.cmpID]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!selectedUserID) {
      setAssignments({});
      return;
    }
    const next = {};
    data.mappings
      .filter((item) => Number(item.userID) === Number(selectedUserID))
      .forEach((item) => {
        next[item.branchID] = {
          branchID: item.branchID,
          counterID: item.counterID || '',
          isDefault: Boolean(item.isDefault),
          canAccess: Boolean(item.canAccess)
        };
      });
    setAssignments(next);
  }, [data.mappings, selectedUserID]);

  const activeBranches = useMemo(() => data.branches.filter((branch) => Boolean(branch.isActive)), [data.branches]);

  const toggleBranch = (branchID, checked) => {
    setAssignments((previous) => {
      const next = { ...previous };
      if (!checked) {
        const wasDefault = next[branchID]?.isDefault;
        delete next[branchID];
        if (wasDefault) {
          const firstBranchID = Number(Object.keys(next)[0]);
          if (firstBranchID) next[firstBranchID] = { ...next[firstBranchID], isDefault: true };
        }
        return next;
      }
      const hasDefault = Object.values(next).some((item) => item.isDefault);
      next[branchID] = { branchID, counterID: '', isDefault: !hasDefault, canAccess: true };
      return next;
    });
  };

  const setDefaultBranch = (branchID) => {
    setAssignments((previous) => Object.fromEntries(
      Object.entries(previous).map(([key, value]) => [key, { ...value, isDefault: Number(key) === Number(branchID) }])
    ));
  };

  const setCounter = (branchID, counterID) => {
    setAssignments((previous) => ({
      ...previous,
      [branchID]: { ...previous[branchID], counterID: counterID || '' }
    }));
  };

  const save = async () => {
    const selectedAssignments = Object.values(assignments);
    if (!selectedUserID) return setMessage({ type: 'error', text: 'Please select a user' });
    if (!selectedAssignments.length) return setMessage({ type: 'error', text: 'Select at least one branch' });
    if (selectedAssignments.filter((item) => item.isDefault).length !== 1) return setMessage({ type: 'error', text: 'Select one default branch' });
    setSaving(true);
    try {
      await axios.post(global.SET_API_URL + 'users/saveBranchAssignments', {
        userID: selectedUserID,
        assignments: selectedAssignments
      });
      setMessage({ type: 'success', text: 'User branch assignments saved successfully' });
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Unable to save assignments' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" sx={{ mb: 1 }}>User Branch Assignments</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Assign one or more branches, choose a default branch, and select a branch-wise cash counter.
        </Typography>

        {message.text && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}
        {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box> : (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select User</InputLabel>
              <Select value={selectedUserID} label="Select User" onChange={(event) => setSelectedUserID(event.target.value)}>
                {data.users.map((user) => (
                  <MenuItem key={user.userID} value={user.userID}>{user.dispName} ({user.userName})</MenuItem>
                ))}
              </Select>
            </FormControl>

            {!activeBranches.length ? <Alert severity="warning">Please create an active branch first.</Alert> : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Access</TableCell>
                      <TableCell>Branch</TableCell>
                      <TableCell>Default</TableCell>
                      <TableCell>Cash Counter</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeBranches.map((branch) => {
                      const assignment = assignments[branch.branchID];
                      const branchCounters = data.counters.filter((counter) => Number(counter.branchID) === Number(branch.branchID) && counter.status === 'active');
                      return (
                        <TableRow key={branch.branchID}>
                          <TableCell>
                            <FormControlLabel
                              control={<Checkbox checked={Boolean(assignment)} onChange={(event) => toggleBranch(branch.branchID, event.target.checked)} />}
                              label={assignment ? 'Allowed' : 'Not allowed'}
                            />
                          </TableCell>
                          <TableCell>{branch.branchName}<Typography variant="caption" display="block">{branch.branchCode}</Typography></TableCell>
                          <TableCell>
                            <Radio checked={Boolean(assignment?.isDefault)} disabled={!assignment} onChange={() => setDefaultBranch(branch.branchID)} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 240 }}>
                            <FormControl fullWidth size="small" disabled={!assignment}>
                              <InputLabel>Cash Counter</InputLabel>
                              <Select value={assignment?.counterID || ''} label="Cash Counter" onChange={(event) => setCounter(branch.branchID, event.target.value)}>
                                <MenuItem value="">No counter</MenuItem>
                                {branchCounters.map((counter) => <MenuItem key={counter.counterID} value={counter.counterID}>{counter.counterName}</MenuItem>)}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" onClick={save} disabled={saving || !activeBranches.length}>
                {saving ? 'Saving...' : 'Save Assignments'}
              </Button>
            </Box>
          </>
        )}
      </Container>
    </>
  );
}